import type { SxProps, Theme } from '@mui/material';

/**
 * The staged treatment's colours, from the Controlled Surfaces tokens (#13440).
 *
 * Nothing here reads the colour scheme: each token already resolves per scheme,
 * and restating that is how the two answers drift apart.
 */

/** The pending ground: the panel's surface, and a staged row's tint. */
export const CS_PENDING_SURFACE = 'var(--custom-surface-cs-light)';

/** A pending pill ON the pending panel — one step deeper, so it reads. */
export const CS_PENDING_SURFACE_STRONG = 'var(--custom-surface-cs-light-hover)';

/** The pending panel's border. */
export const CS_PENDING_BORDER = 'var(--custom-border-cs-default)';

/** The pending accent: panel title, Proposed column, cumulative totals. */
export const CS_PENDING_ACCENT = 'var(--custom-text-cs-default)';

/** The pending panel's left accent bar, and the warning glyph. */
export const CS_PENDING_BAR = 'var(--custom-surface-cs-filled)';

/** The proposed value's colour, the per-row accept tick included. */
export const CS_PROPOSED_TEXT = 'var(--custom-text-cs-proposed)';

/** The outline on a control holding a proposal. */
export const CS_PROPOSED_BORDER = 'var(--custom-border-cs-proposed)';

/** The reject cross, and a premium moving the wrong way. */
export const CS_DANGER_TEXT = 'var(--custom-text-cs-danger)';

export const CS_ACCEPT_SURFACE = 'var(--custom-surface-cs-accept)';
export const CS_ACCEPT_TEXT = 'var(--custom-text-cs-accept)';
export const CS_DANGER_SURFACE = 'var(--custom-surface-cs-danger)';

export const CS_ASSISTANT_SURFACE = 'var(--custom-surface-cs-assistant)';
export const CS_ASSISTANT_TEXT = 'var(--custom-text-cs-assistant)';

/** The underwriter's own count. No `cs` token — the design renders it info. */
export const CS_OPERATOR_SURFACE = 'var(--mantine-color-azure-0)';
export const CS_OPERATOR_TEXT = 'var(--mantine-color-azure-7)';

/**
 * A staged row's ground. A plain declaration rather than an `SxProps`, so a
 * caller can spread it into an `sx` it is already building.
 */
export const stagedRowSx = () => ({
  // `&&` and both selectors, because `CoverageItemContainer` is a JSS styled
  // component that paints odd rows separately — a single `sx` class loses to it
  // and tints only the even rows.
  '&&, &&:nth-child(odd)': {
    background: CS_PENDING_SURFACE,
  },
});

/**
 * The outline on the control that changed. Reaches into the control rather than
 * drawing a box around the cell — the inputs are shared across four personas
 * and cannot take a "staged" prop.
 */
export const stagedControlSx: SxProps<Theme> = {
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: CS_PROPOSED_BORDER,
    borderWidth: 2,
  },
  '& .MuiInput-root:before, & .MuiInput-root:after': {
    borderBottomColor: CS_PROPOSED_BORDER,
    borderBottomWidth: 2,
  },
  '& .MuiSlider-track, & .MuiSlider-thumb': {
    color: CS_PROPOSED_BORDER,
  },
};

/** The committed value, under the control it is being replaced on. */
export const stagedWasSx: SxProps<Theme> = {
  textDecoration: 'line-through',
  // A step under the caption every caller renders it as: the struck figure is
  // the footnote, the control above it the value.
  fontSize: (theme: Theme) =>
    `calc(${theme.typography.caption.fontSize} - 0.1rem)`,
  display: 'block',
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
};
